import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function AuthPage({ mode }) {
  const isRegister = mode === "register";
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const claimTag = params.get("claim");

  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (isRegister) await register(form.email, form.password, form.name);
      else await login(form.email, form.password);
      toast.success(isRegister ? "Account created" : "Signed in");
      navigate(claimTag ? `/tag/${claimTag}` : "/dashboard", { replace: true });
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-start justify-center px-5 sm:px-10 py-16">
      <form onSubmit={submit} className="w-full max-w-sm rise" data-testid="auth-form">
        <p className="text-xs tracking-[0.25em] uppercase font-bold text-muted-foreground">
          {isRegister ? "Create account" : "Owner access"}
        </p>
        <h1 className="font-display text-4xl font-black tracking-tighter mt-3 mb-8">
          {isRegister ? "Register" : "Sign in"}
        </h1>

        {claimTag && (
          <p
            data-testid="claim-context-notice"
            className="mb-6 border border-primary/50 bg-primary/10 px-4 py-3 text-sm"
          >
            Continue to claim tag <span className="font-bold">{claimTag}</span>
          </p>
        )}

        <div className="space-y-5">
          {isRegister && (
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">
                Full name
              </Label>
              <Input
                data-testid="auth-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-2 rounded-none bg-[#141414] border-border h-12 focus:ring-2 focus:ring-primary text-white"
                placeholder="Jane Doe"
              />
            </div>
          )}
          <div>
            <Label className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">
              Email
            </Label>
            <Input
              data-testid="auth-email-input"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-2 rounded-none bg-[#141414] border-border h-12 focus:ring-2 focus:ring-primary text-white"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <Label className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">
              Password
            </Label>
            <Input
              data-testid="auth-password-input"
              type="password"
              required
              minLength={6}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-2 rounded-none bg-[#141414] border-border h-12 focus:ring-2 focus:ring-primary text-white"
              placeholder="••••••••"
            />
          </div>
        </div>

        {error && (
          <p data-testid="auth-error" className="mt-5 text-sm text-primary font-medium">
            {error}
          </p>
        )}

        <Button
          type="submit"
          disabled={busy}
          data-testid="auth-submit-button"
          className="w-full mt-8 h-12 rounded-none bg-primary hover:bg-[#FF5C53] tracking-[0.15em] uppercase text-xs font-bold"
        >
          {busy ? "Please wait" : isRegister ? "Create account" : "Sign in"}
        </Button>

        {!isRegister && (
          <p className="mt-6 text-sm text-muted-foreground">
            <Link
              to="/forgot-password"
              data-testid="forgot-password-link"
              className="hover:text-primary hover:underline"
            >
              Forgot your password?
            </Link>
          </p>
        )}

        <p className="mt-6 text-sm text-muted-foreground">
          {isRegister ? "Already registered?" : "No account yet?"}{" "}
          <Link
            data-testid="auth-switch-link"
            to={`${isRegister ? "/login" : "/register"}${claimTag ? `?claim=${claimTag}` : ""}`}
            className="text-primary font-semibold hover:underline"
          >
            {isRegister ? "Sign in" : "Register"}
          </Link>
        </p>
      </form>
    </div>
  );
}
